import { OrgaConfiguration } from '@/models/orgaConfiguration';

export interface LegalDocuments {
    legalNotice: string;
    termsAndConditions: string;
    privacyPolicy: string;
}

/**
 * Génère automatiquement les documents légaux à partir des informations de l'organisation
 * @param org Configuration de l'organisation
 * @param language Langue des documents (fr, en, es)
 * @returns Les 3 documents légaux générés
 */
export function generateLegalDocuments(
    org: OrgaConfiguration,
    language: 'fr' | 'en' | 'es' = 'fr'
): LegalDocuments {
    const companyName = org.legalInfo?.companyName || org.name;
    const legalId = org.legalInfo?.legalId || '';
    const vatNumber = org.legalInfo?.vatNumber || '';
    const address = org.address;
    const email = org.email || org.afterSalesService?.contacts?.find(c => c.type === 'email')?.value || '';
    const phone = org.phone || org.afterSalesService?.contacts?.find(c => c.type === 'phone')?.value || '';

    const addressString = address
        ? `${address.street || ''}, ${address.postalCode || ''} ${address.city || ''}, ${address.country || ''}`.trim()
        : '';

    return {
        legalNotice: generateLegalNotice(companyName, legalId, vatNumber, addressString, email, phone, language),
        termsAndConditions: generateTermsAndConditions(companyName, legalId, vatNumber, addressString, email, phone, language),
        privacyPolicy: generatePrivacyPolicy(companyName, legalId, vatNumber, addressString, email, phone, language),
    };
}

function generateLegalNotice(
    companyName: string,
    legalId: string,
    vatNumber: string,
    address: string,
    email: string,
    phone: string,
    language: 'fr' | 'en' | 'es'
): string {
    const templates = {
        fr: `
# MENTIONS LÉGALES — ${companyName}
**Dernière mise à jour :** ${new Date().toLocaleDateString('fr-FR')}

**Vendeur / Société :** ${companyName}
**Adresse :** ${address || '[ADRESSE COMPLETE]'}
**Téléphone :** ${phone || '(optionnel)'}
**Email :** ${email || '[EMAIL]'}
**Identifiant légal / Numéro d’enregistrement :** ${legalId || '[IDENTIFIANT LEGAL]'} (SIRET / VAT / Company Number…)
**Numéro de TVA :** ${vatNumber || '(optionnel)'}

## 1) Plateforme technique
La vente est réalisée par **${companyName}** via la plateforme technique **CashLive**, permettant la gestion des commandes, des paiements et des expéditions.

## 2) Paiement
Les paiements sont traités via des prestataires de paiement sécurisés, notamment :
- **Stripe**
- **PayPal**
Les données bancaires ne sont jamais stockées par le vendeur ni par CashLive.

## 3) Livraison
Les expéditions peuvent être gérées via **Sendcloud** et ses transporteurs partenaires, selon les options disponibles.

## 4) Propriété intellectuelle
- Les marques, logos, contenus et visuels liés au vendeur restent sa propriété.
- La plateforme CashLive et ses éléments techniques restent la propriété de CashLive.

## 5) Données personnelles
Pour plus d’informations, veuillez consulter la **Politique de confidentialité**.
        `.trim(),
        en: `
# LEGAL NOTICE — ${companyName}
**Last updated:** ${new Date().toLocaleDateString('en-US')}

**Seller / Company:** ${companyName}
**Address:** ${address || '[FULL ADDRESS]'}
**Phone:** ${phone || '(optional)'}
**Email:** ${email || '[EMAIL]'}
**Legal ID / Registration Number:** ${legalId || '[LEGAL ID]'} (SIRET / VAT / Company Number…)
**VAT Number:** ${vatNumber || '(optional)'}

## 1) Technical Platform
The sale is carried out by **${companyName}** via the **CashLive** technical platform, allowing the management of orders, payments, and shipments.

## 2) Payment
Payments are processed through secure payment providers, including:
- **Stripe**
- **PayPal**
Banking data is never stored by the seller or by CashLive.

## 3) Delivery
Shipments can be managed via **Sendcloud** and its partner carriers, depending on the available options.

## 4) Intellectual Property
- Trademarks, logos, content, and visuals related to the seller remain their property.
- The CashLive platform and its technical elements remain the property of CashLive.

## 5) Personal Data
For more information, please consult the **Privacy Policy**.
        `.trim(),
        es: `
# AVISO LEGAL — ${companyName}
**Última actualización:** ${new Date().toLocaleDateString('es-ES')}

**Vendedor / Empresa:** ${companyName}
**Dirección:** ${address || '[DIRECCIÓN COMPLETA]'}
**Teléfono:** ${phone || '(opcional)'}
**Email:** ${email || '[EMAIL]'}
**ID legal / Número de registro:** ${legalId || '[ID LEGAL]'} (SIRET / VAT / Company Number…)
**Número de IVA:** ${vatNumber || '(opcional)'}

## 1) Plataforma técnica
La venta es realizada por **${companyName}** a través de la plataforma técnica **CashLive**, que permite la gestión de pedidos, pagos y envíos.

## 2) Pago
Los pagos se procesan a través de proveedores de pago seguros, incluyendo:
- **Stripe**
- **PayPal**
Los datos bancarios nunca son almacenados por el vendedor ni por CashLive.

## 3) Entrega
Los envíos pueden ser gestionados a través de **Sendcloud** y sus transportistas asociados, según las opciones disponibles.

## 4) Propiedad intelectual
- Las marcas, logotipos, contenidos y elementos visuales relacionados con el vendedor siguen siendo de su propiedad.
- La plataforma CashLive y sus elementos técnicos siguen siendo propiedad de CashLive.

## 5) Datos personales
Para obtener más información, consulte la **Política de privacidad**.
        `.trim(),
    };

    return templates[language];
}

function generateTermsAndConditions(
    companyName: string,
    legalId: string,
    vatNumber: string,
    address: string,
    email: string,
    phone: string,
    language: 'fr' | 'en' | 'es'
): string {
    const templates = {
        fr: `
# CONDITIONS GÉNÉRALES DE VENTE — ${companyName}
**Dernière mise à jour :** ${new Date().toLocaleDateString('fr-FR')}

**Vendeur / Société :** ${companyName}
**Adresse :** ${address || '[ADRESSE COMPLETE]'}
**Téléphone :** ${phone || '(optionnel)'}
**Email :** ${email || '[EMAIL]'}
**Identifiant légal / Numéro d’enregistrement :** ${legalId || '[IDENTIFIANT LEGAL]'} (SIRET / VAT / Company Number…)
**Numéro de TVA :** ${vatNumber || '(optionnel)'}

## 1) Objet
Les présentes CGV encadrent les ventes réalisées par **${companyName}** via la plateforme technique **CashLive**.
Toute commande implique l’acceptation des présentes CGV.

## 2) Produits et prix
Les produits proposés à la vente sont ceux affichés au moment de la commande.
Les prix affichés sont ceux appliqués au moment du paiement.
Le vendeur peut modifier ses prix à tout moment, sans impact sur les commandes déjà validées.

## 3) Commande
La commande est considérée comme validée une fois le paiement confirmé.
Le vendeur se réserve le droit d’annuler une commande en cas :
- d’échec de paiement
- de suspicion de fraude
- d’anomalie manifeste (erreur de prix, erreur technique, etc.)

## 4) Paiement
Le paiement est effectué via des prestataires sécurisés, notamment :
- **Stripe**
- **PayPal**
Les données bancaires ne sont jamais stockées par le vendeur ni par CashLive.

## 5) Livraison
La livraison est effectuée à l’adresse indiquée par le client au moment de la commande.
L’expédition peut être gérée via **Sendcloud** et ses transporteurs partenaires, selon les options disponibles.
Le client doit vérifier l’état du colis à réception et signaler tout problème dans les meilleurs délais.

## 6) Retours et remboursements
Pour toute demande de retour, de remboursement ou de réclamation, le client doit contacter le vendeur à : **${email}**
Le vendeur proposera une solution selon le cas (échange, remboursement, ou autre), conformément aux règles applicables.

## 7) Responsabilité
Le vendeur est responsable :
- des produits vendus
- de leur conformité
- de leur expédition et livraison
- du service client associé
**CashLive** agit uniquement comme prestataire technique (commande, paiement, expédition).

## 8) Litiges
En cas de litige, une solution amiable sera recherchée en priorité.
À défaut, les tribunaux compétents seront ceux du ressort du siège du vendeur, sauf dispositions légales impératives contraires.
        `.trim(),
        en: `
# TERMS AND CONDITIONS — ${companyName}
**Last updated:** ${new Date().toLocaleDateString('en-US')}

**Seller / Company:** ${companyName}
**Address:** ${address || '[FULL ADDRESS]'}
**Phone:** ${phone || '(optional)'}
**Email:** ${email || '[EMAIL]'}
**Legal ID / Registration Number:** ${legalId || '[LEGAL ID]'} (SIRET / VAT / Company Number…)
**VAT Number:** ${vatNumber || '(optional)'}

## 1) Purpose
These Terms and Conditions govern the sales made by **${companyName}** via the **CashLive** technical platform.
Every order implies acceptance of these Terms and Conditions.

## 2) Products and Prices
The products offered for sale are those displayed at the time of the order.
The prices displayed are those applied at the time of payment.
The seller may modify their prices at any time, without impact on orders already validated.

## 3) Order
The order is considered validated once the payment is confirmed.
The seller reserves the right to cancel an order in case of:
- payment failure
- suspicion of fraud
- manifest anomaly (price error, technical error, etc.)

## 4) Payment
Payment is made via secure providers, including:
- **Stripe**
- **PayPal**
Banking data is never stored by the seller or by CashLive.

## 5) Delivery
Delivery is made to the address indicated by the customer at the time of the order.
Shipment can be managed via **Sendcloud** and its partner carriers, depending on the available options.
The customer must check the status of the package upon receipt and report any problems as soon as possible.

## 6) Returns and Refunds
For any request for return, refund or complaint, the customer must contact the seller at: **${email}**
The seller will propose a solution depending on the case (exchange, refund, or other), in accordance with the applicable rules.

## 7) Liability
The seller is responsible for:
- products sold
- their compliance
- their shipment and delivery
- the associated customer service
**CashLive** acts only as a technical provider (order, payment, shipping).

## 8) Disputes
In the event of a dispute, an amicable solution will be sought first.
Failing this, the competent courts will be those with jurisdiction over the seller's head office, unless mandatory legal provisions to the contrary.
        `.trim(),
        es: `
# CONDICIONES GENERALES DE VENTA — ${companyName}
**Última actualización:** ${new Date().toLocaleDateString('es-ES')}

**Vendedor / Empresa:** ${companyName}
**Dirección:** ${address || '[DIRECCIÓN COMPLETA]'}
**Teléfono:** ${phone || '(opcional)'}
**Email:** ${email || '[EMAIL]'}
**ID legal / Número de registro:** ${legalId || '[ID LEGAL]'} (SIRET / VAT / Company Number…)
**Número de IVA:** ${vatNumber || '(opcional)'}

## 1) Objeto
Las presentes CGV regulan las ventas realizadas por **${companyName}** a través de la plataforma técnica **CashLive**.
Cada pedido implica la aceptación de estas CGV.

## 2) Productos y precios
Los productos ofrecidos a la venta son los que se muestran en el momento del pedido.
Los precios mostrados son los aplicados en el momento del pago.
El vendedor puede modificar sus precios en cualquier momento, sin impacto en los pedidos ya validados.

## 3) Pedido
El pedido se considera validado una vez confirmado el pago.
El vendedor se reserva el derecho de cancelar un pedido en caso de:
- fallo de pago
- sospecha de fraude
- anomalía manifiesta (error de precio, error técnico, etc.)

## 4) Pago
El pago se realiza a través de proveedores seguros, incluyendo:
- **Stripe**
- **PayPal**
Los datos bancarios nunca son almacenados por el vendedor ni por CashLive.

## 5) Entrega
La entrega se realiza a la dirección indicada por el cliente en el momento del pedido.
El envío puede ser gestionado a través de **Sendcloud** y sus transportistas asociados, según las opciones disponibles.
El cliente debe comprobar el estado del paquete al recibirlo y notificar cualquier problema lo antes posible.

## 6) Devoluciones y reembolsos
Para cualquier solicitud de devolución, reembolso o queja, el cliente debe ponerse en contacto con el vendedor en: **${email}**
El vendedor propondrá una solución según el caso (cambio, reembolso u otro), de acuerdo con las normas aplicables.

## 7) Responsabilidad
El vendedor es responsable de:
- productos vendidos
- su cumplimiento
- su envío y entrega
- el servicio de atención al cliente asociado
**CashLive** actúa únicamente como proveedor técnico (pedido, pago, envío).

## 8) Litigios
En caso de litigio, se buscará primero una solución amistosa.
En su defecto, los tribunales competentes serán los de la jurisdicción del domicilio social del vendedor, salvo disposición legal imperativa en contrario.
        `.trim(),
    };

    return templates[language];
}

function generatePrivacyPolicy(
    companyName: string,
    legalId: string,
    vatNumber: string,
    address: string,
    email: string,
    phone: string,
    language: 'fr' | 'en' | 'es'
): string {
    const templates = {
        fr: `
# POLITIQUE DE CONFIDENTIALITÉ — ${companyName}
**Dernière mise à jour :** ${new Date().toLocaleDateString('fr-FR')}

**Vendeur / Société :** ${companyName}
**Adresse :** ${address || '[ADRESSE COMPLETE]'}
**Téléphone :** ${phone || '(optionnel)'}
**Email :** ${email || '[EMAIL]'}
**Identifiant légal / Numéro d’enregistrement :** ${legalId || '[IDENTIFIANT LEGAL]'} (SIRET / VAT / Company Number…)
**Numéro de TVA :** ${vatNumber || '(optionnel)'}

## 1) Informations collectées
Dans le cadre d’une commande ou d’une utilisation du service, nous pouvons collecter les données suivantes :
- **Identité :** nom, prénom
- **Contact :** adresse e-mail, numéro de téléphone
- **Livraison :** adresse postale complète
- **Commande :** produits, montants, statut, historique
- **Données techniques :** adresse IP, navigateur, logs de sécurité et prévention de fraude

⚠️ Les informations bancaires ne sont jamais stockées par le vendeur ni par CashLive. Les paiements sont traités par des prestataires sécurisés tels que **Stripe** et/ou **PayPal**.

## 2) Utilisation des informations
Les données peuvent être utilisées pour :
- traiter et gérer la commande
- gérer le service client, les échanges et réclamations
- gérer l’expédition et la livraison
- envoyer des communications liées à la commande (confirmation, suivi, remboursement, etc.)
- prévenir la fraude et sécuriser le service
- améliorer l’expérience utilisateur et la qualité du service

## 3) Partage des données avec des prestataires
Vos données personnelles peuvent être partagées uniquement si nécessaire avec des prestataires de confiance, notamment :
- **Paiement :** Stripe, PayPal
- **Livraison / Expédition :** Sendcloud, Transporteurs proposés selon disponibilité (ex : Mondial Relay, Chronopost, Colissimo, etc.)
- **Plateforme technique et hébergement :** CashLive (prestataire technique), Firebase (Google) (authentification, stockage et sécurisation)
Nous ne vendons pas vos données personnelles.

## 4) Emails et communications
Nous pouvons vous contacter par e-mail pour :
**A) Emails transactionnels (commande)**
- confirmation de commande, confirmation de paiement, expédition et suivi, remboursement / annulation, support client
**B) Communications informatives**
- Le vendeur et/ou CashLive peuvent envoyer des messages informatifs nécessaires au bon fonctionnement du service.
**C) Nouveautés et promotions (offres commerciales)**
- Des communications commerciales peuvent être envoyées par le vendeur et/ou CashLive si vous y avez consenti ou si cela est permis par la réglementation applicable.
✅ Vous pouvez vous désinscrire à tout moment via le lien présent dans les emails.

## 5) Sécurité et stockage
Nous appliquons des mesures de sécurité pour protéger vos données :
- authentification et contrôle d’accès
- stockage sécurisé et gestion technique via Firebase (Google)
- accès limité aux personnes autorisées
- mesures de prévention contre les accès non autorisés

## 6) Durée de conservation
Les données sont conservées uniquement pendant la durée nécessaire pour traiter les commandes, assurer le service client, respecter les obligations légales et prévenir la fraude.

## 7) Vos droits
Conformément aux règles applicables, vous pouvez demander :
- l’accès à vos données, la rectification, la suppression, l’opposition ou la limitation du traitement.
📩 Pour exercer vos droits, contactez : **${email}**

## 8) Consentement
En utilisant le service, vous acceptez la présente Politique de confidentialité.
        `.trim(),
        en: `
# PRIVACY POLICY — ${companyName}
**Last updated:** ${new Date().toLocaleDateString('en-US')}

**Seller / Company:** ${companyName}
**Address:** ${address || '[FULL ADDRESS]'}
**Phone:** ${phone || '(optional)'}
**Email:** ${email || '[EMAIL]'}
**Legal ID / Registration Number:** ${legalId || '[LEGAL ID]'} (SIRET / VAT / Company Number…)
**VAT Number:** ${vatNumber || '(optional)'}

## 1) Collected Information
In the context of an order or use of the service, we may collect the following data:
- **Identity:** last name, first name
- **Contact:** e-mail address, phone number
- **Delivery:** complete postal address
- **Order:** products, amounts, status, history
- **Technical data:** IP address, browser, security logs and fraud prevention

⚠️ Banking information is never stored by the seller or by CashLive. Payments are processed by secure providers such as **Stripe** and/or **PayPal**.

## 2) Use of Information
Data may be used to:
- process and manage the order
- manage customer service, exchanges and complaints
- manage shipping and delivery
- send communications related to the order (confirmation, tracking, refund, etc.)
- prevent fraud and secure the service
- improve the user experience and service quality

## 3) Data Sharing with Providers
Your personal data may be shared only if necessary with trusted providers, including:
- **Payment:** Stripe, PayPal
- **Delivery / Shipment:** Sendcloud, Carriers proposed based on availability (e.g., Mondial Relay, Chronopost, Colissimo, etc.)
- **Technical Platform and Hosting:** CashLive (technical provider), Firebase (Google) (authentication, storage and security)
We do not sell your personal data.

## 4) Emails and Communications
We may contact you by email for:
**A) Transactional Emails (order)**
- order confirmation, payment confirmation, shipping and tracking, refund / cancellation, customer support
**B) Informative Communications**
- The seller and/or CashLive may send informative messages necessary for the proper functioning of the service.
**C) News and Promotions (commercial offers)**
- Commercial communications may be sent by the seller and/or CashLive if you have consented or if permitted by applicable regulations.
✅ You can unsubscribe at any time via the link in the emails.

## 5) Security and Storage
We apply security measures to protect your data:
- authentication and access control
- secure storage and technical management via Firebase (Google)
- limited access to authorized persons
- prevention measures against unauthorized access

## 6) Retention Period
The data is kept only for the duration necessary to process orders, ensure customer service, comply with legal obligations, and prevent fraud.

## 7) Your Rights
In accordance with applicable rules, you may request:
- access to your data, rectification, deletion, opposition or limitation of processing.
📩 To exercise your rights, contact: **${email}**

## 8) Consent
By using the service, you agree to this Privacy Policy.
        `.trim(),
        es: `
# POLÍTICA DE PRIVACIDAD — ${companyName}
**Última actualización:** ${new Date().toLocaleDateString('es-ES')}

**Vendedor / Empresa:** ${companyName}
**Dirección:** ${address || '[DIRECCIÓN COMPLETA]'}
**Teléfono:** ${phone || '(opcional)'}
**Email:** ${email || '[EMAIL]'}
**ID legal / Número de registro:** ${legalId || '[ID LEGAL]'} (SIRET / VAT / Company Number…)
**Número de IVA:** ${vatNumber || '(opcional)'}

## 1) Información recopilada
En el contexto de un pedido o uso del servicio, podemos recopilar los siguientes datos:
- **Identidad:** nombre, apellidos
- **Contacto:** dirección de correo electrónico, número de teléfono
- **Entrega:** dirección postal completa
- **Pedido:** productos, importes, estado, historial
- **Datos técnicos:** dirección IP, navegador, registros de seguridad y prevención de fraude

⚠️ La información bancaria nunca es almacenada por el vendedor ni por CashLive. Los pagos se procesan a través de proveedores seguros como **Stripe** y/o **PayPal**.

## 2) Uso de la información
Los datos pueden utilizarse para:
- procesar y gestionar el pedido
- gestionar el servicio al cliente, cambios y reclamaciones
- gestionar el envío y la entrega
- enviar comunicaciones relacionadas con el pedido (confirmación, seguimiento, reembolso, etc.)
- prevenir el fraude y asegurar el servicio
- mejorar la experiencia del usuario y la calidad del servicio

## 3) Intercambio de datos con proveedores
Sus datos personales pueden compartirse solo si es necesario con proveedores de confianza, incluyendo:
- **Pago:** Stripe, PayPal
- **Entrega / Envío:** Sendcloud, Transportistas propuestos según disponibilidad (ej: Mondial Relay, Chronopost, Colissimo, etc.)
- **Plataforma técnica y alojamiento:** CashLive (proveedor técnico), Firebase (Google) (autenticación, almacenamiento y seguridad)
No vendemos sus datos personales.

## 4) Correos electrónicos y comunicaciones
Podemos ponernos en contacto con usted por correo electrónico para:
**A) Correos electrónicos transaccionales (pedido)**
- confirmación de pedido, confirmación de pago, envío y seguimiento, reembolso / cancelación, atención al cliente
**B) Comunicaciones informativas**
- El vendedor y/o CashLive pueden enviar mensajes informativos necesarios para el buen funcionamiento del servicio.
**C) Novedades y promociones (ofertas comerciales)**
- El vendedor y/o CashLive peuvent enviar comunicaciones comerciales si usted lo ha consentido o si lo permite la normativa aplicable.
✅ Puede darse de baja en cualquier momento a través del enlace presente en los correos electrónicos.

## 5) Seguridad y almacenamiento
Aplicamos medidas de seguridad para proteger sus datos:
- autenticación y control de acceso
- almacenamiento seguro y gestión técnica a través de Firebase (Google)
- acceso limitado a personas autorizadas
- medidas de prevención contra el acceso no autorizado

## 6) Periodo de conservación
Los datos se conservan únicamente durante el tiempo necesario para tramitar los pedidos, asegurar el servicio al cliente, cumplir con las obligaciones legales y prevenir el fraude.

## 7) Sus derechos
De acuerdo con las normas aplicables, puede solicitar:
- el acceso a sus datos, rectificación, supresión, oposición o limitación del tratamiento.
📩 Para ejercer sus derechos, contacte con: **${email}**

## 8) Consentimiento
Al utilizar el servicio, usted acepta esta Política de privacidad.
        `.trim(),
    };

    return templates[language];
}
